import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentData } from '@angular/fire/compat/firestore';
import { DaoScheduleService } from '../providers/db/dao-schedule.service';
import { DaoSpeakersService } from '../providers/db/dao-speakers.service';
import { Hotsite } from './@types/hotsite';
import { EventDataService } from '../shared/services/eventData/event-data.service';
import { DaoInfoboothService } from '../providers/db/dao-infobooth.service';
import { SponsorProps } from '../modules/list-sponsors/@types/sponsor';
import { DaoSponsorsService } from '../providers/db/dao-sponsors.service';
import { NavController, AlertController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import { PathComponent } from '../models/path/path-components';

@Component({
    selector: 'app-hotsite',
    templateUrl: './hotsite.component.html',
    styleUrls: ['./hotsite.component.scss'],
})
export class HotsiteComponent implements OnInit {
    isLoading: boolean = true;
    eventId: string;
    event: DocumentData;

    eventDate: string;
    eventEndDate: string;
    eventStatus: 'before' | 'during' | 'after' = 'before';
    timeLeft: { months: number; days: number; hours: number; minutes: number; seconds: number } 
    = { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };

    eventLanguage: string = "EnUS";
    schedule: DocumentData[] = [];
    scheduleDates: string[] = [];
    selectedDate: string = '';
    paginatedSchedule: DocumentData[] = [];
    selectedSession: any;
    speakersSelectedSession: DocumentData[] = [];
    speakers: DocumentData[] = [];
    sponsors: SponsorProps[] = [];
    hotsite: Hotsite;
    infoboothModule = null;
    infoboothPage = null;
    defaultSections: string[] = ['infobooth', 'speakers', 'sponsors', 'schedule'];
    
    currentSpeakersPage: number = 0;
    speakersPerPage: number = 8;
    
    currentSponsorsPage: number = 0;
    sponsorsPerPage: number = 8;



    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private navCtrl: NavController,
        private SEventData: EventDataService,
        private dbSchedule: DaoScheduleService,
        private dbSpeakers: DaoSpeakersService,
        private dbSponsors: DaoSponsorsService,
        private SInfobooth: DaoInfoboothService,
        private translateService: TranslateService,
        private alertCtrl: AlertController,
        private modalCtrl: ModalController
    ) { }

    ngOnInit() {
        this.eventId = this.route.snapshot.params['eventId'];
        if (!this.eventId) {
            return;
        }
        this.getEvent();
        this.getSchedule();
        this.getSpeakers();
        this.getSponsors();
        localStorage.removeItem('userToken');
        localStorage.removeItem('userIdentifier');
        localStorage.removeItem('eventId');
        setInterval(() => {
            this.updateEventStatus();
        }, 1000);
    }

    getEvent() {
        this.SEventData.getEventDataSnapshot(this.eventId).then(data => {
            if (!data || !data['hotsite'] || !data['hotsite']['displayHotsite']) {
                return this.navCtrl.back();
            }

            this.event = data;
            this.eventDate = data.startDate;
            this.eventEndDate = data.endDate;
            this.hotsite = data['hotsite'];
            if (!this.hotsite?.sections) {
                this.hotsite.sections = this.defaultSections;
            }

            if (data.language) {
                this.eventLanguage = data.language.replace('_', '');
                this.eventLanguage = this.eventLanguage.charAt(0).toUpperCase() + this.eventLanguage.slice(1);
            } else {
                this.eventLanguage = this.SEventData.getLanguage();
            }

            this.getInfoboothPage();
            this.isLoading = false;
        }).catch(() => {
            this.navCtrl.back();
        });
    }

    getSchedule() {
        this.dbSchedule.getAllSess(this.eventId).subscribe((schedule) => {
            this.scheduleDates = [];

            this.schedule = schedule.map((session) => {
                session.date = String(new Date(session.date).getDate()).padStart(2, '0') + '/' + String(new Date(session.date).getMonth() + 1).padStart(2, '0');
                session.startTime = String(new Date(session.startTime).getHours()).padStart(2, '0') + ':' + String(new Date(session.startTime).getMinutes()).padStart(2, '0');
                session.endTime = String(new Date(session.endTime).getHours()).padStart(2, '0') + ":" + String(new Date(session.endTime).getMinutes()).padStart(2, '0');
                return session;
            });

            this.scheduleDates = <string[]>schedule.reduce((acc, session) => {
                if (!acc.includes(session.date)) {
                    acc.push(session.date);
                }
                return acc;
            }, [])
            this.selectDate(this.scheduleDates[0]);
            this.selectedSession = this.paginatedSchedule[0];
        });
    }

    selectDate(date: string) {
        this.selectedDate = date ?? '';
        this.paginatedSchedule = this.schedule.filter((session) => session.date === this.selectedDate);
    }

    selectSession(session) {
        this.selectedSession = session;
        const speakersUid = Object.keys(session.speakers || {});
        this.speakersSelectedSession = this.speakers.filter((speaker) => speakersUid.includes(speaker.uid));
    }

    getSpeakers() {
        this.dbSpeakers.getSpeakersByEvent(this.eventId).then(data => {
            this.speakers = data;
        })
    }

    getSponsors() {
        this.dbSponsors.fetchSponsorsByEvent(this.eventId)
            .then((sponsors) => {
                this.sponsors = <SponsorProps[]>sponsors;
            })
            .catch(() => {});
    }

    getInfoboothPage() {
        if (!this.hotsite?.infoboothUid) {
            return;
        }
        this.SInfobooth.getPages(this.eventId, this.hotsite.infoboothUid).then(pages => {
            if (pages.length > 0) {
                this.infoboothPage = pages[0];
                if (this.infoboothPage.htmlContent && this.infoboothPage.htmlContent[this.eventLanguage]) {
                    this.infoboothPage.htmlContent[this.eventLanguage] =
                      this.handleIframeDimensions(
                        this.infoboothPage.htmlContent[this.eventLanguage]
                      );
                }
            }
        }).catch(() => {});
    }

    handleIframeDimensions(html: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const iframes = doc.querySelectorAll('iframe');

        iframes.forEach((iframe) => {
          iframe.setAttribute('width', '100%'); 
          iframe.setAttribute('height', '100%');
        });

        return doc.body.innerHTML;
    }

    async openLogin() {
        const eventData = await this.SEventData.getEventDataSnapshot(this.eventId);
        if (!eventData) { return; }

        const modal = await this.modalCtrl.create({
            component: PathComponent.public_login,
            componentProps: {
                eventId: this.eventId,
                logoUrl: eventData.logo?.url
            }
        });
        return await modal.present();
    }

    async openRegister() {
        const eventData = await this.SEventData.getEventDataSnapshot(this.eventId);
        if (!eventData) { return; }

        const modal = await this.modalCtrl.create({
            component: PathComponent.public_register,
            componentProps: {
                eventId: this.eventId,
                logoUrl: eventData.logo?.url
            }
        });
        return await modal.present();
    }

    goToSympla() {
        const symplaUrl = (this.event?.hotsite?.symplaPurchaseUrl)
            || (this.event?.hotsite?.symplaUrl)
            || (this.event?.hotsite?.configHostsite?.symplaUrl)
            || (this.event as any)?.symplaUrl;

        if (symplaUrl) {
            window.open(symplaUrl, '_blank');
        } else {
            alert('URL de compra não configurada. Entre em contato com o organizador.');
        }
    }

    updateEventStatus() {
        if (!this.eventDate) return;
        const now = DateTime.now();
        const startDate = DateTime.fromISO(this.eventDate);
        const endDate = this.eventEndDate ? DateTime.fromISO(this.eventEndDate) : null;
        if (now < startDate) {
            this.eventStatus = 'before';
            this.timeLeft = this.getLeftTime(this.eventDate);
        } else if (!endDate || now <= endDate) {
            this.eventStatus = 'during';
            this.timeLeft = { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
        } else {
            this.eventStatus = 'after';
            this.timeLeft = { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
    }

    getLeftTime(eventDate: string) {
        const now = DateTime.now();
        const target = DateTime.fromISO(eventDate);

        if (target <= now) {
            return { months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        const diff = target.diff(now, ['months', 'days', 'hours', 'minutes', 'seconds']).toObject();
        return {
            months: Math.floor(diff.months || 0),
            days: Math.floor(diff.days || 0),
            hours: Math.floor(diff.hours || 0),
            minutes: Math.floor(diff.minutes || 0),
            seconds: Math.floor(diff.seconds || 0)
        };
    }

    getPaginatedSpeakers(): DocumentData[] {
        const start = this.currentSpeakersPage * this.speakersPerPage;
        const end = start + this.speakersPerPage;
        return this.speakers.slice(start, end);
    }
    
    getSpeakerTitle(speaker: any): string {
        if (!speaker?.title) return '';
        
        if (typeof speaker.title === 'string') {
            return speaker.title;
        }
        
        if (typeof speaker.title === 'object' && speaker.title !== null) {
            return speaker.title[this.eventLanguage] || speaker.title['EnUS'] || speaker.title['PtBR'] || '';
        }
        
        return '';
    }

    nextSpeakersPage(): void {
        const totalPages = this.getTotalSpeakersPages();
        if (this.currentSpeakersPage < totalPages - 1) {
            this.currentSpeakersPage++;
        }
    }

    prevSpeakersPage(): void {
        if (this.currentSpeakersPage > 0) {
            this.currentSpeakersPage--;
        }
    }

    goToSpeakersPage(page: number): void {
        if (page >= 0 && page < this.getTotalSpeakersPages()) {
            this.currentSpeakersPage = page;
        }
    }

    getTotalSpeakersPages(): number {
        return Math.ceil(this.speakers.length / this.speakersPerPage);
    }

    getSpeakersPagesArray(): number[] {
        return Array.from({ length: this.getTotalSpeakersPages() }, (_, i) => i);
    }

    getPaginatedSponsors(): SponsorProps[] {
        const start = this.currentSponsorsPage * this.sponsorsPerPage;
        const end = start + this.sponsorsPerPage;
        return this.sponsors.slice(start, end);
    }

    nextSponsorsPage(): void {
        const totalPages = this.getTotalSponsorsPages();
        if (this.currentSponsorsPage < totalPages - 1) {
            this.currentSponsorsPage++;
        }
    }

    prevSponsorsPage(): void {
        if (this.currentSponsorsPage > 0) {
            this.currentSponsorsPage--;
        }
    }

    goToSponsorsPage(page: number): void {
        if (page >= 0 && page < this.getTotalSponsorsPages()) {
            this.currentSponsorsPage = page;
        }
    }

    getTotalSponsorsPages(): number {
        return Math.ceil(this.sponsors.length / this.sponsorsPerPage);
    }

    getSponsorsPagesArray(): number[] {
        return Array.from({ length: this.getTotalSponsorsPages() }, (_, i) => i);
    }

}
